import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import {
  Award,
  ArrowRight,
  Heart,
  GraduationCap,
  Sparkles,
  Leaf,
  Rocket,
  Users,
  HeartHandshake,
  ShieldCheck,
  Languages,
  Quote,
  CheckCircle2,
} from 'lucide-react'
import { useLanguage } from '../../context/LanguageContext'
import { PageSlugs } from '../../constants/pageSlugs'
import { fadeInUp, staggerContainer, viewportOnce } from '../../utils/animations'

const PILLARS = [
  {
    key: 'valuesCharacter',
    slug: PageSlugs.valuesCharacter,
    icon: Heart,
    bar: 'bg-primary',
    accent: 'bg-primary/10 text-primary ring-primary/20',
  },
  {
    key: 'academicExcellence',
    slug: PageSlugs.academicExcellence,
    icon: Award,
    bar: 'bg-accent',
    accent: 'bg-accent/10 text-accent ring-accent/25',
  },
  {
    key: 'beyondClassroom',
    slug: PageSlugs.beyondClassroom,
    icon: Sparkles,
    bar: 'bg-secondary',
    accent: 'bg-secondary/10 text-secondary ring-secondary/20',
  },
  {
    key: 'sustainability',
    slug: PageSlugs.sustainability,
    icon: Leaf,
    bar: 'bg-primary',
    accent: 'bg-primary/10 text-primary ring-primary/20',
  },
  {
    key: 'universityCareerPathways',
    slug: PageSlugs.universityCareerPathways,
    icon: GraduationCap,
    bar: 'bg-accent',
    accent: 'bg-accent/10 text-accent ring-accent/25',
  },
  {
    key: 'realWorldReadiness',
    slug: PageSlugs.realWorldReadiness,
    icon: Rocket,
    bar: 'bg-secondary',
    accent: 'bg-secondary/10 text-secondary ring-secondary/20',
  },
  {
    key: 'inclusionLearningSupport',
    slug: PageSlugs.inclusionLearningSupport,
    icon: Users,
    bar: 'bg-primary',
    accent: 'bg-primary/10 text-primary ring-primary/20',
  },
  {
    key: 'wellbeingCounseling',
    slug: PageSlugs.wellbeingCounseling,
    icon: HeartHandshake,
    bar: 'bg-accent',
    accent: 'bg-accent/10 text-accent ring-accent/25',
  },
  {
    key: 'studentSafety',
    slug: PageSlugs.studentSafety,
    icon: ShieldCheck,
    bar: 'bg-secondary',
    accent: 'bg-secondary/10 text-secondary ring-secondary/20',
  },
  {
    key: 'arabicNonNative',
    slug: PageSlugs.arabicNonNative,
    icon: Languages,
    bar: 'bg-primary',
    accent: 'bg-primary/10 text-primary ring-primary/20',
  },
]

function ImageTextSection({ image, imageAlt, children, reverse = false }) {
  return (
    <div
      className={`grid items-center gap-8 lg:grid-cols-2 lg:gap-12 ${
        reverse ? 'lg:[&>*:first-child]:order-2' : ''
      }`}
    >
      <div className="relative overflow-hidden rounded-3xl shadow-xl shadow-primary/10">
        <img src={image} alt={imageAlt} className="aspect-[4/3] w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent" />
      </div>
      <div>{children}</div>
    </div>
  )
}

export default function MayarDifferencePageContent() {
  const { t } = useTranslation()
  const { isRTL } = useLanguage()

  return (
    <div className="space-y-16 sm:space-y-20 lg:space-y-24">
      {/* Intro label + title */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={fadeInUp}
        className="mx-auto max-w-3xl text-center"
      >
        <p className="mb-3 text-sm font-bold uppercase tracking-widest text-accent">
          {t('mayarDifferencePage.introLabel')}
        </p>
        <h2 className="text-2xl font-bold text-primary sm:text-3xl md:text-4xl">
          {t('mayarDifferencePage.sectionTitle')}
        </h2>
      </motion.div>

      {/* Image + intro paragraph */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={fadeInUp}
      >
        <ImageTextSection image="/slider/001.JPG" imageAlt={t('mayarDifferencePage.sectionTitle')}>
          <p className="text-lg leading-relaxed text-text/75 sm:text-xl">
            {t('mayarDifferencePage.intro1')}
          </p>
        </ImageTextSection>
      </motion.section>

      {/* Promise callout */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={fadeInUp}
        className="relative overflow-hidden rounded-3xl border border-primary/15 bg-gradient-to-br from-primary/5 via-white to-accent/5 p-6 sm:p-10"
      >
        <Quote className="absolute end-6 top-6 h-16 w-16 text-primary/10 sm:end-10 sm:top-8" aria-hidden />
        <div className="relative max-w-3xl">
          <p className="text-lg font-medium leading-relaxed text-primary sm:text-xl">
            {t('mayarDifferencePage.intro2')}
          </p>
        </div>
      </motion.div>

      {/* Checklist summary */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={staggerContainer}
      >
        <div className="mb-8 sm:mb-10">
          <div className="mx-auto h-1 w-16 rounded-full gradient-brand" aria-hidden />
        </div>

        <ul className="mb-10 grid gap-3 sm:grid-cols-2 lg:hidden">
          {PILLARS.map(({ key }, index) => (
            <motion.li
              key={key}
              variants={fadeInUp}
              className="flex items-start gap-3 rounded-xl border border-border/60 bg-white px-4 py-3 text-sm"
            >
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden />
              <span className="font-medium text-text/80">
                {String(index + 1).padStart(2, '0')}. {t(`mayarDifferencePage.pillars.${key}.title`)}
              </span>
            </motion.li>
          ))}
        </ul>

        <motion.div
          variants={staggerContainer}
          className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
        >
          {PILLARS.map(({ key, slug, icon: Icon, bar, accent }, index) => (
            <motion.div key={key} variants={fadeInUp}>
              <Link
                to={`/pages/${slug}`}
                className="group flex h-full flex-col rounded-2xl border border-border/70 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/25 hover:shadow-xl hover:shadow-primary/10"
              >
                <div className="mb-4 flex items-center justify-between">
                  <span className="text-xs font-bold tabular-nums text-text/40">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className={`h-1 w-10 rounded-full ${bar}`} aria-hidden />
                </div>

                <div
                  className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl ring-1 transition-transform duration-300 group-hover:scale-105 ${accent}`}
                >
                  <Icon className="h-6 w-6" aria-hidden />
                </div>

                <h4 className="mb-2 text-lg font-bold text-primary group-hover:text-accent">
                  {t(`mayarDifferencePage.pillars.${key}.title`)}
                </h4>
                <p className="mb-4 flex-1 text-sm leading-relaxed text-text/65">
                  {t(`mayarDifferencePage.pillars.${key}.description`)}
                </p>

                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-accent">
                  {t('mayarDifferencePage.explorePillar')}
                  <ArrowRight
                    className={`h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 ${isRTL ? 'rotate-180 group-hover:-translate-x-0.5' : ''}`}
                  />
                </span>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </motion.section>

      {/* Full-width banner — pillar titles from original list */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={fadeInUp}
        className="relative overflow-hidden rounded-3xl"
      >
        <img
          src="/slider/003.JPG"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/85 to-primary/70" />
        <div className="relative px-6 py-12 sm:px-12 sm:py-16 lg:px-16">
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-accent">
            {t('mayarDifferencePage.introLabel')}
          </p>
          <h3 className="max-w-2xl text-2xl font-bold text-white sm:text-3xl">
            {t('mayarDifferencePage.sectionTitle')}
          </h3>
          <div className="mt-6 flex flex-wrap gap-2">
            {PILLARS.map(({ key }) => (
              <span
                key={key}
                className="rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm sm:text-sm"
              >
                {t(`mayarDifferencePage.pillars.${key}.title`)}
              </span>
            ))}
          </div>
        </div>
      </motion.section>
    </div>
  )
}
